'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { BriefingPeriod } from './market-briefing-types';
import { PERIOD_BADGES, PLAYBACK_RATES, PlaybackRate } from './audio-player-types';

interface AudioPlayerProps {
  activePeriod: BriefingPeriod;
  title?: string;
  description?: string;
  audioSrc?: string;
  className?: string;
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function AudioPlayer({
  activePeriod,
  title = "Today's Market Sentiment Brief",
  description = 'AI-narrated summary of the top movers and sentiment shifts',
  audioSrc,
  className = ''
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(204); // Default 3:24
  const [playbackRate, setPlaybackRate] = useState<PlaybackRate>(1);

  const badge = PERIOD_BADGES[activePeriod];

  const togglePlay = useCallback(() => {
    if (!audioRef.current) {
      // Demo mode - just toggle state
      setIsPlaying(!isPlaying);
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;

    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
    setCurrentTime(newTime);
  }, [duration]);

  const cyclePlaybackRate = useCallback(() => {
    const currentIndex = PLAYBACK_RATES.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % PLAYBACK_RATES.length;
    const newRate = PLAYBACK_RATES[nextIndex];
    setPlaybackRate(newRate);

    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  }, [playbackRate]);

  // Demo mode: simulate progress when playing
  useEffect(() => {
    if (!audioSrc && isPlaying) {
      const interval = setInterval(() => {
        setCurrentTime(prev => {
          if (prev >= duration) {
            setIsPlaying(false);
            return 0;
          }
          return prev + (0.1 * playbackRate);
        });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [audioSrc, isPlaying, duration, playbackRate]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={cn('', className)}>
      {audioSrc && <audio ref={audioRef} src={audioSrc} preload="metadata" />}

      <div className="card-surface p-5 h-full">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2.5 mb-2">
              <h3 className="font-display text-base font-semibold text-[#e6edf3]">
                Audio Brief
              </h3>
              <div className="h-4 w-px bg-slate-700/50" />
              <span className={cn('px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide', badge.gradient)}>
                {badge.label}
              </span>
            </div>
            <p className="text-reading-sm text-[#8b949e]">{description}</p>
          </div>
          <div className="text-[11px] font-mono text-[#6e7681] bg-[#0d1117] px-2.5 py-1 rounded-lg border border-slate-700/30">
            {formatTime(duration)}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-4">
          {/* Play/Pause Button */}
          <button
            onClick={togglePlay}
            className="w-11 h-11 rounded-full bg-gradient-to-br from-[#d4a853] to-[#b8923f] flex items-center justify-center transition-all hover:scale-105 hover:shadow-lg hover:shadow-[#d4a853]/20 flex-shrink-0"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#0d1117]">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#0d1117] ml-0.5">
                <polygon points="5,3 19,12 5,21" />
              </svg>
            )}
          </button>

          {/* Progress Bar */}
          <div className="flex-1">
            <div
              ref={progressBarRef}
              onClick={handleProgressClick}
              className="h-1.5 bg-[#0d1117] rounded-full overflow-hidden cursor-pointer group"
            >
              <div
                className="h-full bg-gradient-to-r from-[#d4a853] to-[#e8c679] rounded-full transition-all duration-100"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="flex justify-between text-[11px] font-mono text-[#6e7681] mt-1.5">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Speed Control */}
          <button
            onClick={cyclePlaybackRate}
            className="bg-[#0d1117] text-[#8b949e] px-2.5 py-1.5 rounded-lg text-[11px] font-mono cursor-pointer hover:text-[#d4a853] hover:border-[#d4a853]/30 border border-slate-700/30 transition-colors flex-shrink-0"
          >
            {playbackRate}x
          </button>
        </div>
      </div>
    </div>
  );
}
